import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface ScorecardRow {
  adviserId: string;
  adviserName: string | null;
  adviserEmail: string | null;
  dealCount: number;
  activeDealCount: number;
  settledThisMonth: number;
  settledCommissionCentsThisMonth: number;
  activeTrailBookCents: number;
  callsThisWindow: number;
  meetingsThisWindow: number;
  emailsThisWindow: number;
  openTrailLeakageAlerts: number;
}

export interface BrokerScorecardData {
  activityWindowDays: number;
  scorecards: ScorecardRow[];
}

interface State {
  data: BrokerScorecardData | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useBrokerScorecard(
  companyId: string | undefined,
  activityWindowDays: number = 7
): State {
  const [data, setData] = useState<BrokerScorecardData | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(companyId));
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    const fn = httpsCallable<
      { companyId: string; activityWindowDays?: number },
      BrokerScorecardData
    >(functions, 'getBrokerScorecard');
    setLoading(true);
    setError(null);
    fn({ companyId, activityWindowDays })
      .then((r) => setData(r.data))
      .catch((err) => {
        console.error('[useBrokerScorecard] fetch failed', err);
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoading(false));
  }, [companyId, activityWindowDays, refreshKey]);

  return { data, loading, error, refresh };
}
